import { getBrowserStorage, safeStorageGet, parseStoredValue } from "@catchy/region-config/storage-safety";
const MESSAGE_SOURCE = "useLocalStorage";

export const nativeLocationPermissionRequestEvent = "catchy:native-location-permission-request";
export const nativeLocationPermissionResultEvent = "catchy:native-location-permission-result";
export const nativeLocationPermissionAuthorizedEvent = "catchy:native-location-permission-authorized";

const isServerSide = () => typeof window === "undefined";
const getBridgeState = () => {
  if (isServerSide()) return null;
  if (!window.__nativeStorageBridge) window.__nativeStorageBridge = { attached: false, nativeChannelReady: false, port: null };
  return window.__nativeStorageBridge;
};
const parseMessagePayload = (data) => {
  if (typeof data === "string") {
    try { const payload = JSON.parse(data); return payload && typeof payload === "object" ? payload : null; } catch { return null; }
  }
  return data && typeof data === "object" ? data : null;
};
const isNativeMessage = (payload, type) => payload?.source === "catchy-android" && payload.type === type;
const dispatchNativeLocationEvents = (payload) => {
  if (isServerSide()) return;
  const eventName = isNativeMessage(payload, "REQUEST_LOCATION_PERMISSION") ? nativeLocationPermissionRequestEvent
    : isNativeMessage(payload, "LOCATION_PERMISSION_AUTHORIZED") ? nativeLocationPermissionAuthorizedEvent
      : isNativeMessage(payload, "LOCATION_PERMISSION_RESULT") ? nativeLocationPermissionResultEvent : null;
  if (eventName) window.dispatchEvent(new CustomEvent(eventName, { detail: payload }));
};
const clearNativeChannel = () => {
  const state = getBridgeState();
  if (state) { state.port = null; state.nativeChannelReady = false; }
};
const postToTwaPort = (payload) => {
  const state = getBridgeState();
  if (!state?.port) return false;
  try { state.port.postMessage(JSON.stringify(payload)); return true; } catch { clearNativeChannel(); return false; }
};
const postToTwaWindow = (payload) => {
  if (!isServerSide()) window.postMessage(payload, "*");
};
const readLocalStorageSnapshot = () => {
  const snapshot = {};
  if (isServerSide()) return snapshot;
  const storage = getBrowserStorage();
  let totalCharacters = 0;
  try {
    for (let i = 0; i < Math.min(storage?.length || 0, 200); i += 1) {
      const key = storage.key(i);
      if (typeof key !== "string" || key.length > 256 || !/^catchy_(wellington|auckland|chch|otago)_/.test(key) || key.endsWith("_favourite_stop_details")) continue;
      const raw = safeStorageGet(storage, key);
      if (raw === null || totalCharacters + key.length + raw.length > 1024 * 1024) continue;
      const checked = parseStoredValue(key, raw);
      if (!checked.ok) continue;
      snapshot[key] = checked.value;
      totalCharacters += key.length + raw.length;
    }
  } catch { /* Blocked storage yields the safe snapshot collected so far. */ }
  return snapshot;
};
const handleTwaPortMessage = (event) => {
  const payload = parseMessagePayload(event.data);
  if (!payload) return;
  const state = getBridgeState();
  if (isNativeMessage(payload, "channelReady") && state?.port) state.nativeChannelReady = true;
  dispatchNativeLocationEvents(payload);
  if (payload.type === "REQUEST_STATE") postToTwaPort({ source: MESSAGE_SOURCE, type: "STATE_SNAPSHOT", state: readLocalStorageSnapshot() });
};
const announceNativeBridgeReady = (port) => {
  try { port.postMessage(JSON.stringify({ source: "catchy-web", type: "NATIVE_BRIDGE_READY" })); } catch { clearNativeChannel(); }
};
const handleWindowMessage = (event) => {
  const payload = parseMessagePayload(event.data);
  if (payload) {
    const state = getBridgeState();
    if (isNativeMessage(payload, "channelReady") && state) state.nativeChannelReady = true;
    dispatchNativeLocationEvents(payload);
    if (payload.type === "REQUEST_STATE") {
      const snapshot = { source: MESSAGE_SOURCE, type: "STATE_SNAPSHOT", state: readLocalStorageSnapshot() };
      postToTwaPort(snapshot);
      postToTwaWindow(snapshot);
    }
  }
  const port = event.ports?.[0];
  if (!port) return;
  const state = getBridgeState();
  if (!state) return;
  if (state.port && state.port !== port) state.port.removeEventListener("message", handleTwaPortMessage);
  state.port = port;
  state.nativeChannelReady = false;
  port.addEventListener("message", handleTwaPortMessage);
  if (typeof port.start === "function") port.start();
  announceNativeBridgeReady(port);
};

export const initNativeStorageBridge = () => {
  const state = getBridgeState();
  if (!state || state.attached) return;
  state.attached = true;
  window.addEventListener("message", handleWindowMessage);
};
export const hasNativeTwaMessagePort = () => {
  const state = getBridgeState();
  return Boolean(state?.port || state?.nativeChannelReady);
};
export const requestNativeLocationPermission = () => {
  const payload = { source: "catchy-web", type: "REQUEST_ANDROID_LOCATION_PERMISSION" };
  postToTwaPort(payload);
  postToTwaWindow(payload);
};
export const postLocalStorageNativeMessage = (message) => {
  if (isServerSide()) return;
  initNativeStorageBridge();
  const payload = { source: MESSAGE_SOURCE, ...message };
  postToTwaPort(payload);
  postToTwaWindow(payload);
};
